import React, {useState} from 'react';
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {InputTextarea} from "primereact/inputtextarea";
import {useTranslation} from "react-i18next";

import {UserService} from "../../service/UserService";
import {toDateTimeString} from "../../utils/TimeUtil";

const LEGACY_NOTE_ID = 'legacy';

const UserNotes = ({user, hideDialog, onNotesChanged}) => {

    const {t} = useTranslation();
    const userService = new UserService();

    const [notes, setNotes] = useState(user.notes || []);
    const [legacyNote, setLegacyNote] = useState(user.note || '');
    const [newText, setNewText] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editingText, setEditingText] = useState('');
    const [saving, setSaving] = useState(false);

    const applyResponse = (response) => {
        setNotes(response.notes || []);
        setLegacyNote(response.note || '');
        onNotesChanged(user.id, response);
    };

    const entries = () => {
        const sorted = [...notes].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
        if (legacyNote)
            sorted.push({id: LEGACY_NOTE_ID, text: legacyNote, created_at: null, updated_at: null, author: null});
        return sorted;
    };

    const addNote = () => {
        if (!newText.trim())
            return;
        setSaving(true);
        userService.addNote(user.id, newText.trim()).then(response => {
            applyResponse(response);
            setNewText('');
        }).finally(() => setSaving(false));
    };

    const saveEdit = () => {
        if (!editingText.trim())
            return;
        setSaving(true);
        userService.updateNote(user.id, editingId, editingText.trim()).then(response => {
            applyResponse(response);
            setEditingId(null);
        }).finally(() => setSaving(false));
    };

    const deleteNote = (noteId) => {
        if (!window.confirm(t('user.delete_note_confirm')))
            return;
        setSaving(true);
        userService.deleteNote(user.id, noteId).then(applyResponse).finally(() => setSaving(false));
    };

    const noteMeta = (entry) => {
        if (entry.id === LEGACY_NOTE_ID)
            return t('user.legacy_note');
        let meta = toDateTimeString(entry.created_at);
        if (entry.updated_at)
            meta += ' (' + t('user.edited') + ')';
        return meta;
    };

    return <Dialog header={`${t('user.notes')} — ${user.full_name || user.email}`} visible={true}
                   style={{width: '40rem', maxWidth: '95vw'}} modal={true} onHide={hideDialog}>
        <div className="note-add">
            <InputTextarea value={newText} rows={2} autoResize={true} placeholder={t('user.add_note')}
                           onChange={(e) => setNewText(e.target.value)} style={{width: '100%'}}/>
            <Button label={t('actions.add')} icon="pi pi-plus" className="p-button-outlined p-button-success"
                    disabled={saving || !newText.trim()} onClick={addNote}/>
        </div>

        {entries().length === 0 && <p className="note-empty">{t('user.no_notes')}</p>}

        <ul className="note-list">
            {entries().map(entry =>
                <li key={entry.id} className="note-item">
                    {editingId === entry.id
                        ? <div className="note-add">
                            <InputTextarea value={editingText} rows={2} autoResize={true}
                                           onChange={(e) => setEditingText(e.target.value)} style={{width: '100%'}}/>
                            <Button icon="pi pi-check" className="p-button-outlined p-button-success"
                                    tooltip={t('actions.save')} disabled={saving || !editingText.trim()} onClick={saveEdit}/>
                            <Button icon="pi pi-times" className="p-button-outlined p-button-warning"
                                    tooltip={t('actions.cancel')} onClick={() => setEditingId(null)}/>
                        </div>
                        : <>
                            <div className="note-item-body">
                                <div className="note-item-meta">{noteMeta(entry)}</div>
                                <div className="note-item-text">{entry.text}</div>
                            </div>
                            <div className="note-item-actions">
                                <Button icon="pi pi-pencil" className="p-button-text p-button-plain"
                                        tooltip={t('actions.edit')} disabled={saving}
                                        onClick={() => {setEditingId(entry.id); setEditingText(entry.text);}}/>
                                <Button icon="pi pi-trash" className="p-button-text p-button-danger"
                                        tooltip={t('actions.delete')} disabled={saving}
                                        onClick={() => deleteNote(entry.id)}/>
                            </div>
                        </>}
                </li>
            )}
        </ul>
    </Dialog>;
};

export default UserNotes;
